/**
 * Types for the conditional engine, a TypeScript port of the Python conditional engine in TRS
 * The shapes (including snake_case keys) intentionally mirror the dictionaries the Python engine accepts so the same
 * condition payloads can be evaluated on the client and server.
 */

/**
 * Possible operations to be applied on a single expression (e.g. "int_variable >= 10", "bool_variable == True")
 */
export enum Operation {
  EQ = 'EQ', // Equal to
  NE = 'NE', // Not equal to
  GT = 'GT', // Greater than
  GE = 'GE', // Greater than or equal to
  LT = 'LT', // Less than
  LE = 'LE', // Less than or equal to
  IN = 'IN', // In list
  NIN = 'NIN', // Not in list
  ANYIN = 'ANYIN', // Any items in list
}

/**
 * Possible operations to be applied across a set of expressions
 * (e.g. "int_variable >= 10 AND bool_variable == True AND ...").
 */
export enum ConditionOperator {
  AND = 'AND',
  OR = 'OR',
}

/**
 * Condition payloads usually arrive as JSON, so both the enum and its raw string name are accepted. The Python engine
 * also accepts the enum's qualified name (e.g. "Operation.EQ"), so that form is supported as well.
 */
export type OperationName = Operation | `${Operation}` | `Operation.${Operation}`
export type ConditionOperatorName =
  | ConditionOperator
  | `${ConditionOperator}`
  | `ConditionOperator.${ConditionOperator}`

export type ExpressionsResult = Record<string, unknown>

export type ConditionsParam = Record<string, unknown>

/**
 * A leaf node: evaluates `conditionsParam[target] <operation> value`.
 */
export interface Condition {
  target: string
  operation: OperationName
  value: unknown
}

/**
 * A branch node: applies `condition_operator` across the evaluated `expressions`. `expressions_result` is returned
 * when the node is satisfied (unless a satisfied child node provides its own).
 */
export interface ConditionGroup {
  condition_operator: ConditionOperatorName
  expressions: Conditions[]
  expressions_result?: ExpressionsResult | null
}

export type Conditions = Condition | ConditionGroup

/**
 * Mirrors the Python `Tuple[bool, Dict]` return value: [isConditionSatisfied, expressionsResult]
 */
export type EvaluateConditionsResult = [boolean, ExpressionsResult]
